import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";
import { API } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";
import { SLAPill } from "../components/SLAPill";
import { IngestProgressInline } from "../components/IngestProgressInline";
import { RFIResponseButton } from "../components/IngestJobAddOns";

export function IngestInbox() {
  const [jobs, setJobs] = useState([]);
  const [portal, setPortal] = useState("");
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(
    () => axios.get(`${API}/ingest/jobs`).then(r => setJobs(r.data || [])).catch(() => setJobs([])),
    [],
  );
  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any job is still processing so the inline progress
  // bars tick live without the user manually reloading. Polls every 2 s and
  // stops as soon as no job is in `processing` status.
  useEffect(() => {
    const anyProcessing = jobs.some(j => j.ingest_status === "processing");
    if (!anyProcessing) return undefined;
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, [jobs, load]);

  const submit = async () => {
    if (!file && !link.trim()) { setMsg("Provide a ZIP file or a Drive link."); return; }
    setBusy(true); setMsg("Uploading… large packages can take 1-3 minutes to process in the background.");
    const fd = new FormData();
    if (portal) fd.append("portal_type", portal);
    if (link.trim()) fd.append("drive_link", link.trim());
    if (file) fd.append("file", file);
    try {
      const r = await axios.post(`${API}/ingest`, fd, { headers:{"Content-Type":"multipart/form-data"}, timeout: 60000 });
      const jobId = r.data.job_id;
      setMsg(`Queued — processing in the background (job ${jobId.slice(0,16)}…)`);
      setFile(null); setLink("");
      // Poll every 2 s for up to 6 minutes
      const started = Date.now();
      const POLL_MS = 2000;
      const TIMEOUT_MS = 6 * 60 * 1000;
      while (Date.now() - started < TIMEOUT_MS) {
        await new Promise(res => setTimeout(res, POLL_MS));
        try {
          const p = await axios.get(`${API}/ingest/jobs/${jobId}`, { timeout: 15000 });
          const status = p?.data?.ingest_status;
          if (status === "ready") {
            const nr = (p.data.needs_review || []).length;
            setMsg(`✅ Ingested ${p.data.top_dir || jobId}. ${nr} item(s) need your sorting.`);
            load();
            return;
          }
          if (status === "error") {
            setMsg(`❌ Ingest failed: ${p.data.error || "(no details)"}`);
            load();
            return;
          }
          // still processing — update message with elapsed time
          const sec = Math.round((Date.now() - started) / 1000);
          setMsg(`Processing… ${sec}s elapsed (job ${jobId.slice(0,16)}…)`);
        } catch (pollErr) {
          // Transient (network blip, brief 502 during a hot reload) — keep polling
          // but surface the cause so failures aren't invisible.
          console.warn(`Poll failed for ${jobId} — retrying in ${POLL_MS}ms`, pollErr?.message || pollErr);
        }
      }
      setMsg("⌛ Still running after 6 minutes — open the inbox row to follow progress.");
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || e?.message || "Ingest failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="container py-8 max-w-7xl" data-testid="inbox-page">
      <div className="label-upper mb-1">Workspace</div>
      <h1 className="font-heading text-3xl font-bold mb-1">Ingest Inbox</h1>
      <p className="text-sm text-slate-500 mb-6">Drop a SmartyGrants ZIP or paste a Google Drive share link. The tool will parse Form 306 (EW / IRW / EPAR), walk the evidence tree, classify every file, and surface anything ambiguous for your sorting.</p>

      <div className="card p-5 mb-8">
        <h3 className="font-heading text-base font-semibold mb-3">New package</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label-upper">Portal (optional hint)</label>
            <select className="input mt-1" value={portal} onChange={e=>setPortal(e.target.value)} data-testid="ingest-portal-select">
              <option value="">Auto-detect from filename</option>
              <option>EW Finalisation</option>
              <option>IRW Scope Approval</option>
              <option>IRW Finalisation</option>
              <option>EPAR Scope Approval</option>
              <option>EPAR Finalisation</option>
            </select>
          </div>
          <div>
            <label className="label-upper">Google Drive share link</label>
            <input className="input mt-1" placeholder="https://drive.google.com/file/d/…" value={link} onChange={e=>setLink(e.target.value)} data-testid="ingest-link-input" />
          </div>
          <div>
            <label className="label-upper">…or upload a ZIP</label>
            <input type="file" accept=".zip" className="input mt-1" onChange={e=>setFile(e.target.files?.[0]||null)} data-testid="ingest-file-input" />
            <div className="text-[10.5px] text-slate-500 mt-1">Cloudflare ingress caps direct uploads at ~100 MB. For larger packages, use the Drive link.</div>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button className="btn-primary flex items-center gap-2" disabled={busy} onClick={submit} data-testid="ingest-submit-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>} Ingest package
          </button>
          {msg && <div className="text-[12px] text-slate-700" data-testid="ingest-status">{msg}</div>}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold">Ingested packages</h3>
          <button className="btn-secondary !py-1.5 !px-2.5 text-[12px]" onClick={load}>Refresh</button>
        </div>
        {jobs.length === 0 ? (
          <div className="px-5 py-8 text-sm text-slate-500">Nothing yet.</div>
        ) : (
          <table className="dense">
            <thead><tr><th>Package</th><th>Portal</th><th>AGRN / Claimant</th><th>Items</th><th>Photos</th><th>SLA</th><th>Open issues</th><th>Submission</th><th></th></tr></thead>
            <tbody>
              {jobs.map(j => {
                const status = j?.ingest_status;
                const isProcessing = status === "processing";
                const isError = status === "error";
                const open = (j.needs_review || []).filter(x => !x.resolved).length;
                const agrn = j?.form_306?.header?.agrn || j?.pdf_meta?.agrn;
                const claimant = j?.form_306?.header?.claimant || j?.pdf_meta?.claimant;
                const needsSorting = !agrn || !claimant;
                const itemCount = j?.form_306?.item_count
                  || j?.slip_estimates_rollup?.count
                  || j?.cet_rollup?.dm_count
                  || 0;
                const kinds = j?.file_kinds || {};
                const photoCount = (kinds.evidence_photo || 0) + (kinds.image || 0);
                return (
                  <tr key={j.id} data-testid={`job-row-${j.id}`} className={isProcessing ? "bg-blue-50/40" : isError ? "bg-red-50/40" : ""}>
                    <td className="font-mono text-[12px]">
                      {j.top_dir_display || j.top_dir || j.id}
                      {isProcessing && <IngestProgressInline progress={j.ingest_progress} jobId={j.id} />}
                      {isError && <div className="text-[10px] text-red-700 font-semibold tracking-[0.14em] uppercase mt-0.5" data-testid={`job-error-${j.id}`} title={j.error || ""}>Failed — {String(j.error || "").slice(0, 50)}</div>}
                    </td>
                    <td>{j.portal_type || j.portal_hint || "—"}</td>
                    <td className="font-mono text-[12px]" data-testid={`job-agrn-${j.id}`}>
                      {agrn ? (
                        <>
                          <span data-testid={`job-agrn-value-${j.id}`}>{agrn}</span>
                          <div className="text-[11px] text-slate-500 font-sans">{claimant || ""}</div>
                        </>
                      ) : isProcessing ? (
                        <span className="text-[11px] text-slate-400">…</span>
                      ) : (
                        <span data-testid={`job-needs-sorting-${j.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] tracking-[0.12em] uppercase font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Needs sorting
                        </span>
                      )}
                    </td>
                    <td className="font-mono">{itemCount || (isProcessing ? "…" : "—")}</td>
                    <td className="font-mono">{photoCount || (isProcessing ? "…" : "—")}</td>
                    <td>{isProcessing ? <span className="text-[11px] text-slate-400">…</span> : <SLAPill sla={j.sla} compact testid={`job-sla-${j.id}`} />}</td>
                    <td>{isProcessing ? <span className="text-[11px] text-slate-400">…</span> : open > 0 ? <span className="pill pill-rfi">{open}</span> : <span className="pill pill-approved">0</span>}</td>
                    <td>{j.submission_id ? <Link to={`/submission/${j.submission_id}`} className="text-xs text-blue-600 hover:underline font-mono">{j.submission_claim_ref || "open"}</Link> : (needsSorting && !isProcessing ? <Link to={`/inbox/${j.id}`} className="text-[11px] text-amber-700 hover:underline" data-testid={`job-needs-sorting-link-${j.id}`}>Needs sorting</Link> : <span className="text-[11px] text-slate-400">—</span>)}</td>
                    <td>
                      <div className="flex flex-col items-stretch gap-1" style={{minWidth: 100}}>
                        <Link to={`/inbox/${j.id}`} className="text-xs text-blue-600 hover:underline">{isProcessing ? "Open →" : "Sort →"}</Link>
                        {!isProcessing && j.submission_id && (
                          <RFIResponseButton job={j} onChange={load} />
                        )}
                        <button
                          type="button"
                          data-testid={`inbox-delete-claim-${j.id}`}
                          onClick={async () => {
                            if (!window.confirm(`Delete this claim?\n\n${j.top_dir_display || j.top_dir || j.id}\n\nThis removes the ingest job, its linked submission and all on-disk evidence. This cannot be undone.`)) return;
                            try {
                              await axios.delete(`${API}/ingest/jobs/${j.id}`);
                              load();
                            } catch (e) {
                              alert("Delete failed: " + (e?.response?.data?.detail || e?.message));
                            }
                          }}
                          className="text-[10.5px] uppercase tracking-wider font-semibold text-red-600 hover:text-red-800 hover:underline"
                        >Delete claim</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

